import dynamic from 'next/dynamic';
import { useState, useRef, Fragment } from 'react';
import { useLocationState } from '../../context/location';
import { quarterConverter, toMoney } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import Radio from '../Radio';
import Input from '../Input';
import BuildingObjectsTablePreview from './BuildingObjectsTablePreview';
import styles from '../../styles/modules/building/BuildingObjectsTable.module.scss';
import FormAgreement from '../FormAgreement';

const Modal = dynamic(() => import('../Modal'));

const getShortTitle = (t) => {
  if (t === 'studio') return 'СТ';

  return `${t}К`;
};
const getMediumTitle = (t) => {
  if (t === 'studio') return 'Студия';

  return `${t}-комнатная`;
};
const getLongTitle = (t) => {
  if (t === 'studio') return 'Квартира-студия';

  switch (t) {
    case 1:
      return 'Однокомнатная квартира';

    case 2:
      return 'Двухкомнатная квартира';

    case 3:
      return 'Трехкомнатная квартира';

    case 4:
      return 'Четырехкомнатная квартира';

    default:
      return '';
  }
};

const BuildingObjectsTable = ({ filters, buildings }) => {
  const locationState = useLocationState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [currentBuilding, setCurrentBuilding] = useState(0);
  const [squareColumns, setSquareColumns] = useState(
    buildings[currentBuilding]['flats-squares'].map((v) => Number(v) + 1)
  );
  const [isOverCell, setIsOverCell] = useState(false);
  const [cellData, setCellData] = useState(null);
  const [clientX, setClientX] = useState(0);
  const [clientY, setClientY] = useState(0);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();

  const isUnderFilter = (v) => {
    let isFiltered = true;

    const getPrice = () => {
      return filters.priceUnit === 'all' ? v.price.value : v.priceM2;
    };

    /** ROOMS -> converting into string because checkboxes is string values. */
    if (!filters.rooms.includes((v.studio ? 'studio' : v.rooms).toString()) && filters.rooms.length)
      isFiltered = false;

    /** PRICE -> super bad code, sry, but this ain't FrontEnd job, so.... */
    if (isFiltered) {
      if (filters.priceFrom.length && filters.priceTo.length) {
        if (!(Number(filters.priceFrom) <= getPrice() && Number(filters.priceTo) >= getPrice()))
          isFiltered = false;
      } else if (filters.priceFrom.length) {
        if (!(Number(filters.priceFrom) <= getPrice())) isFiltered = false;
      } else if (filters.priceTo.length) {
        if (!(Number(filters.priceTo) >= getPrice())) isFiltered = false;
      }
    }

    /** SQUARE */
    if (isFiltered) {
      if (filters.squareFrom.length && filters.squareTo.length) {
        if (
          !(
            Number(filters.squareFrom) <= Number(v.area.value) &&
            Number(filters.squareTo) >= Number(v.area.value)
          )
        )
          isFiltered = false;
      } else if (filters.squareFrom.length) {
        if (!(Number(filters.squareFrom) <= Number(v.area.value))) isFiltered = false;
      } else if (filters.squareTo.length) {
        if (!(Number(filters.squareTo) >= Number(v.area.value))) isFiltered = false;
      }
    }

    return isFiltered;
  };
  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        pathName: window.location.pathname,
        whatComponent: 'BuildingObjectsTable',
        adsNumber: cellData['ads-number'],
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) setIsSubmited(true);
      });
    }
  };

  return (
    <>
      <Modal
        button={!isSubmited && 'Получить консультацию'}
        action={handleFormSubmit}
        visibility={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        buttonDisabled={!agreeWithPolicy}
      >
        {isSubmited ? (
          <Fragment>
            <div style={{ marginBottom: 18 }}>Спасибо за заявку. Мы скоро с Вами свяжемся!</div>
            <img
              src="/images/thumbUp.png"
              alt=""
              style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
            />
          </Fragment>
        ) : (
          <Fragment>
            {cellData && (
              <div className={styles.smallPopPreview}>
                <div className={styles.smallPopPreviewImage}>
                  <img src={cellData.plan} alt="Планировка" />
                </div>
                <div className={styles.smallPopPreviewTitle}>
                  {getLongTitle(cellData.studio ? 'studio' : cellData.rooms)}
                </div>
                <div className={styles.smallPopPreviewLabels}>
                  <span>{getMediumTitle(cellData.studio ? 'studio' : cellData.rooms)}</span>
                  <span>{Number(cellData.area.value).toFixed(2)} м²</span>
                  <span>{toMoney(cellData.price.value)} ₽</span>
                </div>
                <div className={styles.smallPopPreviewFree}>Услуги компании бесплатно</div>
              </div>
            )}
            <div style={{ margin: '18px -12px', borderBottom: '1px dashed rgba(0, 0, 0, 0.05)' }} />
            <Input
              title="Имя"
              inputs={[
                {
                  placeholder: 'Василий Петрович',
                  value: clientName,
                  type: 'text'
                }
              ]}
              isWrappedStyle
              ref={clientNameId}
              onChange={(v) => setClientName(v[0])}
            />
            <Input
              title="Телефон"
              inputs={[
                {
                  placeholder: locationState.phone,
                  value: clientPhone,
                  mask: 'phone',
                  type: 'tel'
                }
              ]}
              isWrappedStyle
              ref={clientPhoneId}
              onChange={(v) => setClientPhone(v[0])}
            />
            <FormAgreement onChange={setAgreeWithPolicy} />
          </Fragment>
        )}
      </Modal>
      <div className={styles.buildingsList}>
        <Radio
          radios={buildings.map((value, index) => {
            return {
              text: (
                <div className={styles.building}>
                  <div className={styles.buildingTitle}>{value['building-section']}</div>
                  <div className={styles.buildingDescription}>
                    {quarterConverter(value['ready-quarter'])} квартал {value['built-year']}
                  </div>
                </div>
              ),
              value: index
            };
          })}
          onChange={(newValue) => {
            setCurrentBuilding(newValue);
            setSquareColumns(buildings[newValue]['flats-squares'].map((v) => Number(v) + 1));
          }}
          selected={currentBuilding}
        />
      </div>
      <div className={styles.table}>
        <div className={styles.tableFixedColumn}>
          <div className={styles.tableFixedColumnText}>Этаж</div>
          {[...Array(buildings[currentBuilding]['floors-total']).keys()]
            .map((value) => value + 1)
            .reverse()
            .map((value, index) => (
              <div key={index}>{value}</div>
            ))}
        </div>
        <div
          className={styles.tableScrollColumn}
          style={{
            gridTemplateColumns: squareColumns.map(() => '1fr').join(' ')
          }}
        >
          <BuildingObjectsTablePreview data={cellData} show={isOverCell} x={clientX} y={clientY} />
          {squareColumns.map((value, index) => (
            <div className={[styles.tableCell, styles.heading].join(' ')} key={index}>
              от {value} м²
            </div>
          ))}
          {Object.entries(buildings[currentBuilding]['flats-by-floor'])
            .reverse()
            .map((value) => {
              return value[1].map((v, i) => {
                /** 0. Updating column square value. */
                if (v && v.area.value < Number(squareColumns[i])) {
                  squareColumns[i] = v.area.value;
                  setSquareColumns(squareColumns);
                }

                /** 1. If no value presented - adding empty cell. */
                if (!v)
                  return <div className={[styles.tableCell, styles.empty].join(' ')} key={i} />;

                /** 2. Otherwise addign full data. */
                return (
                  <div
                    className={[styles.tableCell, styles.default, isUnderFilter(v) && styles.exist]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseEnter={() => {
                      setCellData(v);
                      setIsOverCell(true);
                    }}
                    onMouseLeave={() => setIsOverCell(false)}
                    onMouseMove={(e) => {
                      setClientX(e.clientX);
                      setClientY(e.clientY);
                    }}
                    onClick={() => {
                      setCellData(v);
                      setIsModalVisible(true);
                    }}
                    key={i}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.tableCellContent} style={{ fontWeight: 600 }}>
                      {getShortTitle(v.studio ? 'studio' : v.rooms)}
                    </div>
                    <div className={styles.tableCellContent}>
                      {filters.priceUnit === 'all'
                        ? `${Number((v.price.value / 1000000).toFixed(2))} млн/₽`
                        : `${toMoney(v.priceM2)} м²/₽`}
                    </div>
                  </div>
                );
              });
            })}
        </div>
      </div>
    </>
  );
};

export default BuildingObjectsTable;
